(function (root, factory) {
  const api = factory()

  if (typeof module === 'object' && module.exports) {
    module.exports = api
  }

  if (root) {
    root.LegacyPageMigration = api
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict'

  const LEGACY_SENSOR_FUNCTION_PATTERN = /^\s*(getSensorPopup(?:FromPLC)?)\s*\(/i
  const LEGACY_CONTROL_FUNCTION_PATTERN = /^\s*(?:(?:https?:)?\/\/[^/\s]+\/|\/)?(ControlPopup_OPEN)\s*\(/i
  const LEGACY_CONTROL_POPUP_TYPES = Object.freeze({
    ONF1: 'control-onoff',
    ONF2: 'control-onoff-auto',
    ONF3: 'control-onf3',
    OR1: 'control-proportional',
    OR4: 'control-setpoint'
  })
  const IMAGE_FIELD_KEYS = ['background-image', 'background_image', 'backgroundImage', 'bitlampImageOn', 'bitlampImageOff', 'flashState1Image', 'flashState2Image']

  function canonicalizeLegacyFilePath(value) {
    if (typeof value !== 'string') return value

    return value
      .replace(/\\/g, '/')
      .replace(/(url\(\s*['"]?)(?:\.\.\/|\.\/)?Files\//gi, '$1Files/')
      .replace(/^(?:\.\.\/|\.\/|\/)+Files\//i, 'Files/')
  }

  function getBackgroundImageUrl(value) {
    if (typeof value !== 'string') return ''
    const match = value.match(/url\(\s*['"]?([^'")]+)['"]?\s*\)/i)
    return match ? canonicalizeLegacyFilePath(match[1]) : ''
  }

  function normalizeLegacyMediaPaths(element) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) return element

    let changed = false
    const normalizedElement = { ...element }
    for (const key of IMAGE_FIELD_KEYS) {
      const normalizedValue = canonicalizeLegacyFilePath(normalizedElement[key])
      if (normalizedValue !== normalizedElement[key]) {
        normalizedElement[key] = normalizedValue
        changed = true
      }
    }

    // Very old pages stored the image only in the CSS `background` shorthand.
    // New renderers read a dedicated image field, so preserve the extracted URL.
    const hasDedicatedImage = IMAGE_FIELD_KEYS.some(key => String(normalizedElement[key] || '').trim())
    if (!hasDedicatedImage) {
      const legacyImageUrl = getBackgroundImageUrl(normalizedElement.background)
      if (legacyImageUrl) {
        normalizedElement['background-image'] = legacyImageUrl
        changed = true
      }
    }

    return changed ? normalizedElement : element
  }

  function migrateBooleanTagBinding(element, booleanTagNames) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) return element
    if (String(element.elementType || '').toLowerCase() === 'bitlamp') return element
    if (String(element.boolTagName || '').trim()) return element

    const tagName = String(element.TagName || '').trim()
    if (!tagName || !booleanTagNames || !booleanTagNames.has(tagName.toLowerCase())) return element

    const migratedElement = { ...element, boolTagName: tagName }
    if (!String(migratedElement.boolExpected || '').trim()) migratedElement.boolExpected = 'true'
    delete migratedElement.TagName
    return migratedElement
  }

  function decodeEscapedCharacter(source, index) {
    const character = source[index]
    const simpleEscapes = {
      '0': '\0',
      b: '\b',
      f: '\f',
      n: '\n',
      r: '\r',
      t: '\t',
      v: '\v'
    }

    if (Object.prototype.hasOwnProperty.call(simpleEscapes, character)) {
      return { value: simpleEscapes[character], nextIndex: index + 1 }
    }

    if (character === 'x') {
      const hex = source.slice(index + 1, index + 3)
      if (/^[0-9a-f]{2}$/i.test(hex)) {
        return { value: String.fromCharCode(parseInt(hex, 16)), nextIndex: index + 3 }
      }
    }

    if (character === 'u') {
      const hex = source.slice(index + 1, index + 5)
      if (/^[0-9a-f]{4}$/i.test(hex)) {
        return { value: String.fromCharCode(parseInt(hex, 16)), nextIndex: index + 5 }
      }
    }

    // JavaScript'in eski string literal davranışıyla uyumlu olarak bilinmeyen
    // kaçışlarda ters eğik çizgiyi kaldırıp karakteri koru (örn. \_ -> _).
    return { value: character || '', nextIndex: index + 1 }
  }

  function readQuotedArgument(source, startIndex) {
    let index = startIndex
    while (/\s/.test(source[index] || '')) index += 1

    const quote = source[index]
    if (quote !== "'" && quote !== '"') return null

    index += 1
    let value = ''

    while (index < source.length) {
      const character = source[index]
      if (character === quote) {
        return { value, nextIndex: index + 1 }
      }

      if (character === '\\') {
        const decoded = decodeEscapedCharacter(source, index + 1)
        value += decoded.value
        index = decoded.nextIndex
        continue
      }

      value += character
      index += 1
    }

    return null
  }

  function parseLegacySensorPopup(value) {
    if (typeof value !== 'string') return null

    const functionMatch = value.match(LEGACY_SENSOR_FUNCTION_PATTERN)
    if (!functionMatch) return null

    let index = functionMatch[0].length
    const args = []

    for (let argIndex = 0; argIndex < 3; argIndex += 1) {
      const argument = readQuotedArgument(value, index)
      if (!argument) return null

      args.push(argument.value)
      index = argument.nextIndex
      while (/\s/.test(value[index] || '')) index += 1

      if (argIndex < 2) {
        if (value[index] !== ',') return null
        index += 1
      }
    }

    while (/\s/.test(value[index] || '')) index += 1
    if (value[index] !== ')') return null
    index += 1
    while (/\s/.test(value[index] || '')) index += 1
    if (value[index] === ';') index += 1
    while (/\s/.test(value[index] || '')) index += 1
    if (index !== value.length) return null

    return {
      functionName: functionMatch[1],
      source: /FromPLC$/i.test(functionMatch[1]) ? 'plc' : 'history',
      tagName: args[0],
      displayName: args[1],
      unit: args[2]
    }
  }

  function decodeLegacyUrlValue(value) {
    const normalizedValue = String(value || '').replace(/\+/g, ' ')
    try {
      return decodeURIComponent(normalizedValue)
    } catch (error) {
      return normalizedValue
    }
  }

  function parseLegacyControlPopup(value) {
    if (typeof value !== 'string') return null

    const functionMatch = value.match(LEGACY_CONTROL_FUNCTION_PATTERN)
    if (!functionMatch) return null

    let index = functionMatch[0].length
    const args = []
    for (let argIndex = 0; argIndex < 4; argIndex += 1) {
      const argument = readQuotedArgument(value, index)
      if (!argument) return null

      args.push(decodeLegacyUrlValue(argument.value))
      index = argument.nextIndex
      while (/\s/.test(value[index] || '')) index += 1

      if (argIndex < 3) {
        if (value[index] !== ',') return null
        index += 1
      }
    }

    while (/\s/.test(value[index] || '')) index += 1
    if (value[index] !== ')') return null
    index += 1
    while (/\s/.test(value[index] || '')) index += 1
    if (value[index] === ';') index += 1
    while (/\s/.test(value[index] || '')) index += 1
    if (index !== value.length) return null

    const legacyType = String(args[2] || '').trim().toUpperCase()
    const popupType = LEGACY_CONTROL_POPUP_TYPES[legacyType]
    if (!popupType) return null

    const parsedRole = Number(args[3])
    return {
      functionName: functionMatch[1],
      tagName: args[0],
      displayName: args[1],
      legacyType,
      popupType,
      authLevel: Number.isFinite(parsedRole) ? parsedRole : 0
    }
  }

  function normalizePageElement(element) {
    if (!element || typeof element !== 'object' || Array.isArray(element)) {
      return element
    }

    const mediaNormalizedElement = normalizeLegacyMediaPaths(element)
    const legacySensor = parseLegacySensorPopup(mediaNormalizedElement.navigate)
    const legacyControl = parseLegacyControlPopup(mediaNormalizedElement.navigate)
    if (!legacySensor && !legacyControl) return mediaNormalizedElement

    if (legacyControl) {
      const normalizedElement = {
        ...mediaNormalizedElement,
        navigate: '',
        popup: legacyControl.popupType,
        popupTagName: legacyControl.tagName || mediaNormalizedElement.popupTagName || mediaNormalizedElement.TagName || mediaNormalizedElement.TagId || '',
        PopOver: true
      }

      if (!normalizedElement.PopOverTitle && legacyControl.displayName) {
        normalizedElement.PopOverTitle = legacyControl.displayName
      }
      if (normalizedElement.UserRole === undefined || normalizedElement.UserRole === null || normalizedElement.UserRole === '') {
        normalizedElement.UserRole = legacyControl.authLevel
      }
      return normalizedElement
    }

    const normalizedElement = {
      ...mediaNormalizedElement,
      navigate: '',
      popup: 'sensor-chart',
      popupTagName: legacySensor.tagName || mediaNormalizedElement.popupTagName || mediaNormalizedElement.TagName || mediaNormalizedElement.TagId || ''
    }

    if (!normalizedElement.PopOverTitle && legacySensor.displayName) {
      normalizedElement.PopOverTitle = legacySensor.displayName
    }
    if (!normalizedElement.Birim && legacySensor.unit) {
      normalizedElement.Birim = legacySensor.unit
    }

    return normalizedElement
  }

  function normalizePageElements(pageData) {
    if (!Array.isArray(pageData)) return pageData

    let hasChanges = false
    const normalizedData = pageData.map(element => {
      const normalizedElement = normalizePageElement(element)
      if (normalizedElement !== element) hasChanges = true
      return normalizedElement
    })

    return hasChanges ? normalizedData : pageData
  }

  function migratePageElements(pageData, booleanTagNames) {
    if (!Array.isArray(pageData)) return pageData

    let hasChanges = false
    const migratedData = pageData.map(element => {
      const normalizedElement = normalizePageElement(element)
      const migratedElement = migrateBooleanTagBinding(normalizedElement, booleanTagNames)
      if (migratedElement !== element) hasChanges = true
      return migratedElement
    })

    return hasChanges ? migratedData : pageData
  }

  return {
    canonicalizeLegacyFilePath,
    getBackgroundImageUrl,
    migrateBooleanTagBinding,
    migratePageElements,
    normalizeLegacyMediaPaths,
    normalizePageElement,
    normalizePageElements,
    parseLegacyControlPopup,
    parseLegacySensorPopup
  }
})
